<script lang="ts">
    import type {Thread} from 'src/features/threads/types';
    import {ThreadsService} from 'src/features/threads/services/threads.service';
    import {createThreadsContext} from 'src/features/threads/threads.context';
    import {getInjector} from 'core/svelte-context/context';
    import {onMount} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import KpPopover from 'shared/ui-widgets/popover/KpPopover.svelte';
    import ThreadContent from 'src/features/threads/thread-content/ThreadContent.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import ThreadsList from 'src/features/threads/popover/components/ThreadsList.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import NewThreadPopoverPage from 'src/features/threads/popover/components/new-thread/NewThreadPopoverPage.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import ThreadPopoverHeader from 'src/features/threads/popover/components/ThreadPopoverHeader.svelte';

    const service = getInjector().getByClass(ThreadsService);
    const context = createThreadsContext(service);

    let loading = true;
    let userThreads: Thread[];

    let page: 'main' | 'new-thread' | 'thread-content' = 'main';
    let animationDirection = 0;
    let selectedThread: Thread | null = null;

    onMount(async () => {
        userThreads = await service.getUserThreads();
        loading = false;

        context.setUserThreads(userThreads);
    });

    const handleThreadSelectedOrCreated = (event: CustomEvent<Thread>) => {
        selectedThread = event.detail;
        animationDirection = -1;
        page = 'thread-content';
    };

    const handleGoToNewThreadPage = () => {
        animationDirection = 1;
        page = 'new-thread';
    };

    const handleGoToMainPage = () => {
        animationDirection = page === 'new-thread' ? -1 : 1;
        selectedThread = null;
        page = 'main';
    };
</script>

<KpPopover buttonSize="md"
           placement="bottom-end"
           buttonStyle="no-background"
           additionalPopoverClasses="kp-threads-popover"
           additionalPopoverPanelClasses="kp-threads-popover-panel"
           additionalClasses="kp-threads-popover-button">

    <UIcon slot="button" icon="messages"/>

    <div slot="popover-content" class="kp-threads-popover-content">
        {#if loading}
            <div class="centered-container">
                <KpLoadingBlock size="sm"/>
            </div>
        {/if}

        {#if !loading}
            {#key page}
                <div class="anim-container" in:fly={{x: 15 * animationDirection, duration: 250}}>
                    {#if page === 'main' || page === 'thread-content'}
                        <ThreadsList {selectedThread}
                                     on:new-thread={handleGoToNewThreadPage}
                                     on:thread-select={handleThreadSelectedOrCreated}/>

                        {#if !exists(selectedThread)}
                            <div class="centered-container">
                                <IconedContent gap="0px"
                                               icon="messages"
                                               orientation="vertical"
                                               align="center"
                                               justify="center">

                                    Vyberte nebo vytvořte konverzaci
                                </IconedContent>
                            </div>
                        {/if}

                        {#if exists(selectedThread)}
                            {#key selectedThread}
                                <div class="anim-container" in:fly={{y: 10, duration: 250}}>
                                    <ThreadPopoverHeader on:close={handleGoToMainPage} thread="{selectedThread}"/>
                                    <ThreadContent placement="popover" thread="{selectedThread}"/>
                                </div>
                            {/key}
                        {/if}
                    {/if}

                    {#if page === 'new-thread'}
                        <NewThreadPopoverPage on:back={handleGoToMainPage}
                                              on:thread-created={handleThreadSelectedOrCreated}/>
                    {/if}
                </div>
            {/key}
        {/if}
    </div>
</KpPopover>

<style lang="less">
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro-erp.less";

    .kp-threads-popover-content {
        padding: @spacing-s 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        gap: @spacing-m;

        .anim-container {
            .flex-grow();
        }

        .centered-container {
            .flex-grow();
            align-items: center;
            justify-content: center;
        }
    }

    :global {
        .kp-threads-popover {
            .kp-popover-animation-container {
                height: 100%;
            }

            .popover {
                margin-top: @spacing-s;
                background-color: @themed-body-bg;
                border-radius: @border-radius-xl;
                border: 1px solid @themed-border-default;

                .popover-content {
                    display: flex;
                    flex-direction: column;
                    background-color: @themed-body-bg;
                    padding: 0;
                    height: 100%;

                    .kp-threads-popover-content {
                        .flex-grow();
                        padding: 0;
                        gap: 0;
                    }
                }
            }

            .kp-button {
                outline: none;
            }
        }

        .kp-threads-popover-panel {
            width: 800px;
            height: 960px;
            max-width: 90vw;
            max-height: 90vh;
        }
    }
</style>